import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Reads the `sso_error` the backend appends to /login after a failed SSO attempt and returns
// the translated message plus a function to dismiss it. The parameter is removed from the URL
// so a reload does not show the error again.
export const useSSOError = () => {
    const { t } = useTranslation()
    const [reason, setReason] = useState(() => {
        const params = new URLSearchParams(window.location.search)
        const value = params.get('sso_error')
        if (value) {
            params.delete('sso_error')
            const search = params.toString()
            window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}`)
        }
        return value
    })
    const clear = useCallback(() => setReason(null), [])

    const messages = {
        sso_not_enabled: t('ssoErrorNotEnabled'),
        sso_not_configured: t('ssoErrorNotEnabled'),
        provider_not_supported: t('ssoErrorNotEnabled'),
        user_not_found: t('ssoErrorAccountNotFound'),
        domain_not_allowed: t('ssoErrorAccountNotFound'),
        // Deliberately explicit rather than the generic "account not found" -- support handles
        // these by hand today, so naming the real cause saves a round trip to the backend logs.
        // Revisit if that changes (e.g. once duplicates are prevented or auto-merged).
        multiple_accounts: t('ssoErrorDuplicateAccounts'),
        identity_conflict: t('ssoErrorAccountNotFound'),
        personal_account_not_allowed: t('ssoErrorPersonalAccount'),
        user_disabled: t('ssoErrorUserDisabled'),
        cancelled: t('ssoErrorCancelled'),
        // The school's Microsoft tenant / Google domain doesn't match the one already recorded
        // for it (auto-pinned on an earlier login) -- see the SSO-without-Tenant-ID redesign.
        tenant_mismatch: t('ssoErrorTenantMismatch'),
        // First-time login where the provider didn't cryptographically vouch for the email's
        // domain (Microsoft's xms_edov claim not configured yet, or similar for Google).
        unverified_email: t('ssoErrorUnverifiedEmail'),
        // IT Access is web-only (same restriction as password login) -- only reachable from the
        // student/mobile clients since WEB_CLIENTS already exempts teacher.
        it_access_web_only: t('ssoErrorItAccessWebOnly'),
    }

    return [reason ? messages[reason] || t('ssoErrorGeneric') : '', clear]
}
